import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '../../../config/api';
import { MAX_ANALYTICS_LIMIT } from '../../../config/constants';
import { debugError } from '../../../utils/debugLog';

// Request deduplication - prevent multiple identical requests
const pendingRequests = new Map();

/**
 * Custom hook for fetching template analytics from MV-based endpoint
 *
 * This hook replaces multiple API calls with a single optimized call to:
 * - /api/v1/templates/analytics/templates (regular listing, max limit: MAX_ANALYTICS_LIMIT)
 * - /api/v1/templates/analytics/templates/search (search, max limit: MAX_ANALYTICS_LIMIT)
 *
 * Benefits:
 * - Single API call instead of multiple
 * - Pre-computed analytics (faster response)
 * - All analytics data included in one response
 * - Supports filtering, pagination, and search
 * - Automatically caps limit based on endpoint constraints
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Number of templates to fetch (default: 50)
 *   - Will be capped to MAX_ANALYTICS_LIMIT for both endpoints
 * @param {number} options.offset - Pagination offset (default: 0)
 * @param {string} options.category - Filter by category
 * @param {string} options.status - Filter by status (draft, active, deprecated, archived)
 * @param {string} options.searchQuery - Search query (3+ chars triggers search endpoint)
 * @param {boolean} options.enabled - Whether to fetch immediately (default: true)
 */
export const useTemplateAnalyticsMV = ({
  limit = 50,
  offset = 0,
  category = null,
  status = null,
  searchQuery = null,
  enabled = true,
} = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track last request key to prevent duplicate sequential calls
  const lastRequestKeyRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Determine which endpoint to use based on search query
  const endpoint = useMemo(() => {
    if (searchQuery && searchQuery.trim().length >= 3) {
      // Use search endpoint when search query is provided
      return '/api/v1/templates/analytics/templates/search';
    }
    // Use base templates endpoint for listing
    return '/api/v1/templates/analytics/templates';
  }, [searchQuery]);

  // Cap limit based on endpoint constraints
  // Both endpoints now have max limit of MAX_ANALYTICS_LIMIT
  const effectiveLimit = useMemo(() => {
    // Both search and regular endpoints support up to MAX_ANALYTICS_LIMIT items
    return Math.min(limit, MAX_ANALYTICS_LIMIT);
  }, [limit]);

  // Build query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    if (searchQuery && searchQuery.trim().length >= 3) {
      // Search endpoint parameters
      params.append('q', searchQuery.trim());
      params.append('limit', effectiveLimit.toString());
      if (category && category !== 'all') {
        params.append('category', category);
      }
      if (status && status !== 'all') {
        params.append('status', status);
      }
    } else {
      // Base templates endpoint parameters
      // Always include limit and offset to ensure pagination works correctly
      params.append('limit', String(effectiveLimit));
      params.append('offset', String(offset));
      if (category && category !== 'all') {
        params.append('category', category);
      }
      if (status && status !== 'all') {
        params.append('status', status);
      }
    }

    return params.toString();
  }, [effectiveLimit, offset, category, status, searchQuery]);

  // Fetch templates with analytics (with deduplication)
  const fetchTemplates = useCallback(async () => {
    if (!enabled) return;

    // Create a unique request key for deduplication
    const requestKey = `${endpoint}?${queryParams}`;

    // Check if this exact request is already pending
    if (pendingRequests.has(requestKey)) {
      // Return the existing promise instead of making a new request
      return pendingRequests.get(requestKey);
    }

    // Check if we're already fetching the same request
    if (lastRequestKeyRef.current === requestKey && isFetchingRef.current) {
      // Same request as last one and still fetching - skip to prevent duplicate sequential calls
      return null;
    }

    // Mark as fetching
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    // Create the request promise
    const requestPromise = (async () => {
      try {
        const url = `${endpoint}?${queryParams}`;
        const response = await apiFetch(url);

        if (!response || !response.success) {
          throw new Error(
            response?.message || 'Failed to fetch templates from API'
          );
        }

        setData(response);
        lastRequestKeyRef.current = requestKey; // Track successful request
        isFetchingRef.current = false;
        return response;
      } catch (err) {
        // Handle network errors (e.g., CORS, connection refused, timeout)
        let errorMessage = 'Failed to load templates';

        if (err.message === 'Failed to fetch') {
          errorMessage =
            'Network error: Unable to connect to API. Please check your connection and ensure the API server is running.';
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        debugError('Error fetching template analytics:', {
          error: err,
          endpoint,
          queryParams,
          message: errorMessage,
        });
        // Don't re-throw to prevent unhandled promise rejection
        return null;
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
        // Remove from pending requests
        pendingRequests.delete(requestKey);
      }
    })();

    // Store the promise in pending requests
    pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  }, [endpoint, queryParams, enabled]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    if (enabled) {
      // Use a small delay to batch rapid changes and prevent duplicate requests
      const timeoutId = setTimeout(() => {
        fetchTemplates().catch(err => {
          // Error already handled in fetchTemplates, just log here for debugging
          debugError('[useTemplateAnalyticsMV] Fetch error in useEffect:', err);
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, queryParams, enabled]); // Use endpoint and queryParams instead of fetchTemplates to avoid infinite loops

  // Transform response for component consumption
  const templates = useMemo(() => {
    if (!data || !data.templates) return [];

    // Map MV response to component-expected format
    return data.templates.map(template => ({
      // Basic template fields (map from MV response)
      id: template.template_id,
      template_id: template.template_id,
      name: template.template_name,
      template_name: template.template_name,
      category: template.template_category,
      template_category: template.template_category,
      description: template.template_description, // Now available from MV
      template_description: template.template_description,
      version: template.version_number,
      version_number: template.version_number,
      status: template.version_status,
      version_status: template.version_status,
      template: template.template, // template_content from template_versions join
      output_format: template.output_format, // From template_versions join
      tags: Array.isArray(template.tags)
        ? template.tags
        : template.tags
          ? Object.values(template.tags)
          : [], // Handle jsonb tags
      created_at: template.created_at || template.version_created_at, // Template-level created_at or version_created_at
      version_created_at: template.version_created_at,

      // Analytics fields - spread directly so CardCompact can use them
      // This matches what CardCompact expects in the analytics prop
      total_executions: template.total_executions || 0,
      successful_executions: template.successful_executions || 0,
      failed_executions: template.failed_executions || 0,
      success_rate_percentage: template.success_rate_percentage || 0,
      avg_execution_time: template.avg_execution_time || 0,
      avg_cost: template.avg_cost || 0,
      total_cost: template.total_cost || 0,
      total_feedback_count: template.total_feedback_count || 0,
      avg_rating: template.avg_rating || 0,
      min_rating: template.min_rating || null,
      max_rating: template.max_rating || null,
      positive_feedback_count: template.positive_feedback_count || 0,
      negative_feedback_count: template.negative_feedback_count || 0,
      feedback_coverage_percentage: template.feedback_coverage_percentage || 0,
      weighted_rating_score: template.weighted_rating_score || 0,
      total_content_checks: template.total_content_checks || 0,
      avg_risk_score: template.avg_risk_score || 0,
      last_activity_at: template.last_activity_at,

      // Policy fields from template-level assignments
      policy_enabled: template.policy_enabled || false,
      policy_count: template.policy_count || 0,
      policies: template.policies || [],

      // Also provide as analytics object for backward compatibility
      analytics: {
        total_executions: template.total_executions || 0,
        successful_executions: template.successful_executions || 0,
        failed_executions: template.failed_executions || 0,
        success_rate_percentage: template.success_rate_percentage || 0,
        avg_execution_time: template.avg_execution_time || 0,
        avg_cost: template.avg_cost || 0,
        total_cost: template.total_cost || 0,
        total_feedback_count: template.total_feedback_count || 0,
        avg_rating: template.avg_rating || 0,
        min_rating: template.min_rating || null,
        max_rating: template.max_rating || null,
        positive_feedback_count: template.positive_feedback_count || 0,
        negative_feedback_count: template.negative_feedback_count || 0,
        feedback_coverage_percentage:
          template.feedback_coverage_percentage || 0,
        weighted_rating_score: template.weighted_rating_score || 0,
        total_content_checks: template.total_content_checks || 0,
        avg_risk_score: template.avg_risk_score || 0,
        last_activity_at: template.last_activity_at,
        // Policy fields
        policy_enabled: template.policy_enabled || false,
        policy_count: template.policy_count || 0,
        policies: template.policies || [],
      },

      // Pass through all other fields from MV response
      ...template,
    }));
  }, [data]);

  // Pagination metadata from API response
  const totalCount = data?.total_count || 0;
  const limitValue = data?.limit ?? effectiveLimit;
  const offsetValue = data?.offset ?? offset;

  // Extract summary data
  const summary = useMemo(() => {
    if (!data) return null;

    return {
      total_count: data.total_count || 0,
      total_templates: data.summary?.total_templates || data.total_count || 0,
      total_executions: data.summary?.total_executions || 0,
      total_feedback: data.summary?.total_feedback || 0,
      avg_success_rate: data.summary?.avg_success_rate || 0,
      avg_rating: data.summary?.avg_rating || 0,
      total_cost: data.summary?.total_cost || 0,
    };
  }, [data]);

  // Extract categories data
  const categories = useMemo(() => {
    if (!data || !data.categories) return {};
    return data.categories;
  }, [data]);

  // Extract top performers
  const topPerformers = useMemo(() => {
    if (!data || !data.top_performers) return [];
    return data.top_performers;
  }, [data]);

  // Extract recent activity
  const recentActivity = useMemo(() => {
    if (!data || !data.recent_activity) return [];
    return data.recent_activity;
  }, [data]);

  return {
    // Main data
    templates,
    summary,
    categories,
    topPerformers,
    recentActivity,

    // Pagination
    totalCount,
    limit: limitValue,
    offset: offsetValue,

    // Raw response (for advanced usage)
    rawData: data,

    // State
    loading,
    error,

    // Actions
    refetch: fetchTemplates,
    refresh: fetchTemplates,
  };
};

export default useTemplateAnalyticsMV;
